/**
 * Content pages carry both languages in one file, in the same commit, as the
 * content README requires. The body is split by two marker lines:
 *
 *   <<en>>
 *   English paragraphs...
 *   <<es>>
 *   Parrafos en espanol...
 *
 * A page with no markers is shown as written in both languages, which keeps
 * an old single body page readable rather than blank.
 */

export type Lang = 'es' | 'en';

const MARKER = /^<<(en|es)>>\s*$/;

export function splitBilingual(body: string): Record<Lang, string> {
  const out: Record<Lang, string[]> = { en: [], es: [] };
  let current: Lang | null = null;
  let sawMarker = false;

  for (const line of body.split(/\r?\n/)) {
    const m = MARKER.exec(line.trim());
    if (m) {
      current = m[1] as Lang;
      sawMarker = true;
      continue;
    }
    if (current) out[current].push(line);
  }

  if (!sawMarker) return { en: body.trim(), es: body.trim() };
  return { en: out.en.join('\n').trim(), es: out.es.join('\n').trim() };
}

export function bodyFor(body: string, lang: Lang): string {
  const parts = splitBilingual(body);
  return parts[lang] || parts[lang === 'en' ? 'es' : 'en'];
}

/** The markers a writer leaves where a fact is still missing. */
const PLACEHOLDER = /\[(?:TIM TO CONFIRM|PHOTO TO SUPPLY)[^\]]*\]/g;

/**
 * Makes an unfilled placeholder impossible to miss and impossible to mistake
 * for real copy. Works on already escaped HTML, so it can only add a mark
 * around text that is there.
 */
export function highlightPlaceholders(html: string): string {
  return html.replace(
    PLACEHOLDER,
    (found) =>
      `<mark class="rounded-sm border border-dashed border-mark bg-transparent px-1 text-mark">${found}</mark>`,
  );
}

export function countPlaceholders(text: string): number {
  return (text.match(PLACEHOLDER) ?? []).length;
}
